import React, { useState, useRef } from 'react';
import { signInWithEmailAndPassword, signOut } from 'firebase/auth';
import { auth } from '../firebase';
import toast from 'react-hot-toast';
import {
  useListings, useLeads,
  addListing, updateListing, deleteListing as deleteListingFn,
  updateLead, uploadPhotos
} from '../hooks/useFirestore';

const BRANDS = ['Surron', 'Talaria', 'Kugoo', 'Zero', 'Segway', 'Other'];
const CONDITIONS = ['Brand New', 'Like New', 'Good', 'Fair'];
const BATTERY_CONDITIONS = ['Excellent', 'Good', 'Fair', 'Needs Replacement'];
const CATEGORIES = [
  { id: 'surron', label: 'Surron' },
  { id: 'talaria', label: 'Talaria' },
  { id: 'dirt', label: 'Electric Dirt Bikes' },
  { id: 'scooter', label: 'Electric Scooters' },
  { id: 'used', label: 'Used E-Bikes' },
  { id: 'new', label: 'New Arrivals' },
];
const STATUSES = ['New Lead', 'Contacted', 'Inspection Pending', 'Listed', 'Sold', 'Rejected'];

function statusClass(s) {
  const map = { 'New Lead': 'status-new', 'Contacted': 'status-contacted', 'Inspection Pending': 'status-inspection', 'Listed': 'status-listed', 'Sold': 'status-sold', 'Rejected': 'status-rejected' };
  return `status-badge ${map[s] || ''}`;
}

export default function Admin() {
  const [user, setUser] = useState(auth.currentUser);
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [logging, setLogging] = useState(false);

  React.useEffect(() => {
    const unsub = auth.onAuthStateChanged(setUser);
    return unsub;
  }, []);

  const handleLogin = async () => {
    setLogging(true);
    try {
      await signInWithEmailAndPassword(auth, email, password);
      toast.success('Logged in!');
    } catch (err) {
      toast.error(err.message || 'Login failed');
    }
    setLogging(false);
  };

  if (!user) {
    return (
      <div className="fade-in" style={{ maxWidth: 400, margin: '0 auto', padding: '80px 20px', textAlign: 'center' }}>
        <div style={{ fontSize: 48, marginBottom: 16 }}>🛡️</div>
        <h2 style={{ marginBottom: 24 }}>Admin Login</h2>
        <div style={{ display: 'flex', flexDirection: 'column', gap: 12 }}>
          <input type="email" placeholder="Email" value={email} onChange={e => setEmail(e.target.value)} />
          <input type="password" placeholder="Password" value={password} onChange={e => setPassword(e.target.value)} onKeyDown={e => e.key === 'Enter' && handleLogin()} />
          <button className="btn-volt" style={{ width: '100%', justifyContent: 'center' }} onClick={handleLogin} disabled={logging}>
            {logging ? 'Logging in...' : 'Login'}
          </button>
        </div>
      </div>
    );
  }

  return <AdminDashboard user={user} />;
}

function AdminDashboard({ user }) {
  const { allListings: listings, loading: listingsLoading } = useListings();
  const { leads, loading: leadsLoading } = useLeads();
  const [tab, setTab] = useState('listings');
  const [editingListing, setEditingListing] = useState(null);
  const [editingLead, setEditingLead] = useState(null);
  const [showAdd, setShowAdd] = useState(false);

  const stats = {
    active: listings.filter(l => l.status === 'Listed').length,
    total: listings.length,
    sold: listings.filter(l => l.status === 'Sold').length,
    newLeads: leads.filter(l => l.status === 'New Lead').length,
  };

  const handleSaveListing = async (data, newFiles) => {
    try {
      let photoUrls = data.photos || [];

      if (newFiles && newFiles.length > 0) {
        toast('Uploading photos...', { icon: '📷' });
        const listingId = data.id || 'new_' + Date.now();
        const newUrls = await uploadPhotos('listings', listingId, newFiles);
        photoUrls = [...photoUrls, ...newUrls];
      }

      if (data.id) {
        await updateListing(data.id, { ...data, photos: photoUrls });
        toast.success('Listing updated');
      } else {
        await addListing({ ...data, photos: photoUrls });
        toast.success('Listing added');
      }
      setEditingListing(null);
      setShowAdd(false);
    } catch (err) {
      console.error(err);
      toast.error('Error saving listing');
    }
  };

  const handleDeleteListing = async (id) => {
    if (!window.confirm('Delete this listing?')) return;
    try {
      await deleteListingFn(id);
      toast.success('Listing deleted');
    } catch (err) {
      toast.error('Error deleting');
    }
  };

  const handleUpdateLead = async (data) => {
    try {
      await updateLead(data.id, data);
      toast.success('Lead updated');
      setEditingLead(null);
    } catch (err) {
      toast.error('Error updating lead');
    }
  };

  return (
    <div className="fade-in" style={{ maxWidth: 1200, margin: '0 auto', padding: '24px 20px' }}>
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 24 }}>
        <h1 style={{ fontSize: 24 }}>Admin Dashboard</h1>
        <div style={{ display: 'flex', gap: 12, alignItems: 'center' }}>
          <span style={{ color: 'var(--text-3)', fontSize: 12 }}>{user.email}</span>
          <button className="btn-outline btn-sm" onClick={() => signOut(auth)}>Logout</button>
        </div>
      </div>

      {/* Stats */}
      <div style={{ display: 'grid', gridTemplateColumns: 'repeat(4, 1fr)', gap: 12, marginBottom: 24 }}>
        {[
          ['Active', stats.active, 'var(--volt)'],
          ['Total', stats.total, 'var(--info)'],
          ['Sold', stats.sold, 'var(--text-3)'],
          ['New Leads', stats.newLeads, 'var(--warning)'],
        ].map(([label, val, color]) => (
          <div key={label} style={{ background: 'var(--dark-2)', border: '1px solid var(--dark-4)', borderRadius: 12, padding: 16 }}>
            <p style={{ fontSize: 11, color: 'var(--text-3)', textTransform: 'uppercase', letterSpacing: 0.5, marginBottom: 4 }}>{label}</p>
            <p style={{ fontSize: 28, fontWeight: 700, fontFamily: "'Space Grotesk'", color }}>{val}</p>
          </div>
        ))}
      </div>

      {/* Tabs */}
      <div className="tab-bar" style={{ marginBottom: 24 }}>
        <button className={tab === 'listings' ? 'active' : ''} onClick={() => setTab('listings')}>Listings ({listings.length})</button>
        <button className={tab === 'leads' ? 'active' : ''} onClick={() => setTab('leads')}>Seller Leads ({leads.length})</button>
      </div>

      {/* Listings Tab */}
      {tab === 'listings' && (
        <div>
          <div style={{ marginBottom: 16 }}>
            <button className="btn-volt btn-sm" onClick={() => setShowAdd(true)}>+ Add Listing</button>
          </div>
          {listingsLoading ? <p style={{ color: 'var(--text-2)' }}>Loading...</p> : (
            <div style={{ overflowX: 'auto' }}>
              <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: 13 }}>
                <thead>
                  <tr style={{ borderBottom: '1px solid var(--dark-4)' }}>
                    {['Photo', 'Bike', 'Brand', 'Price', 'Condition', 'Status', 'Actions'].map(h => (
                      <th key={h} style={{ textAlign: 'left', padding: '10px 12px', color: 'var(--text-3)', fontSize: 11, textTransform: 'uppercase', letterSpacing: 0.5 }}>{h}</th>
                    ))}
                  </tr>
                </thead>
                <tbody>
                  {listings.map(l => (
                    <tr key={l.id} style={{ borderBottom: '1px solid var(--dark-4)' }}>
                      <td style={{ padding: '10px 12px' }}>
                        {l.photos && l.photos.length > 0 ? (
                          <img src={l.photos[0]} alt="" style={{ width: 48, height: 48, objectFit: 'cover', borderRadius: 6 }} />
                        ) : (
                          <div style={{ width: 48, height: 48, borderRadius: 6, background: 'var(--dark-3)', display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: 20 }}>🏍️</div>
                        )}
                      </td>
                      <td style={{ padding: '10px 12px', fontWeight: 500 }}>{l.name}</td>
                      <td style={{ padding: '10px 12px', color: 'var(--text-2)' }}>{l.brand}</td>
                      <td style={{ padding: '10px 12px', color: 'var(--volt)', fontWeight: 600 }}>AED {l.price?.toLocaleString()}</td>
                      <td style={{ padding: '10px 12px', color: 'var(--text-2)' }}>{l.condition}</td>
                      <td style={{ padding: '10px 12px' }}><span className={statusClass(l.status)}>{l.status}</span></td>
                      <td style={{ padding: '10px 12px' }}>
                        <div style={{ display: 'flex', gap: 8 }}>
                          <button className="btn-outline btn-sm" onClick={() => setEditingListing(l)}>Edit</button>
                          <button className="btn-danger" onClick={() => handleDeleteListing(l.id)}>Del</button>
                        </div>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </div>
      )}

      {/* Leads Tab */}
      {tab === 'leads' && (
        <div>
          {leadsLoading ? <p style={{ color: 'var(--text-2)' }}>Loading...</p> : leads.length === 0 ? (
            <p style={{ color: 'var(--text-2)', textAlign: 'center', padding: 40 }}>No seller leads yet.</p>
          ) : (
            <div style={{ overflowX: 'auto' }}>
              <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: 13 }}>
                <thead>
                  <tr style={{ borderBottom: '1px solid var(--dark-4)' }}>
                    {['Seller', 'Phone', 'Bike', 'Price', 'Photos', 'Status', 'Actions'].map(h => (
                      <th key={h} style={{ textAlign: 'left', padding: '10px 12px', color: 'var(--text-3)', fontSize: 11, textTransform: 'uppercase', letterSpacing: 0.5 }}>{h}</th>
                    ))}
                  </tr>
                </thead>
                <tbody>
                  {leads.map(l => (
                    <tr key={l.id} style={{ borderBottom: '1px solid var(--dark-4)' }}>
                      <td style={{ padding: '10px 12px', fontWeight: 500 }}>{l.name}</td>
                      <td style={{ padding: '10px 12px' }}>
                        <a href={`https://wa.me/${l.phone?.replace(/\D/g, '')}`} target="_blank" rel="noopener noreferrer" style={{ color: '#25D366', textDecoration: 'none' }}>{l.phone}</a>
                      </td>
                      <td style={{ padding: '10px 12px', color: 'var(--text-2)' }}>{l.brand} {l.model}</td>
                      <td style={{ padding: '10px 12px', color: 'var(--volt)' }}>AED {Number(l.askingPrice).toLocaleString()}</td>
                      <td style={{ padding: '10px 12px' }}>
                        {l.photos && l.photos.length > 0 ? (
                          <div style={{ display: 'flex', gap: 4 }}>
                            {l.photos.slice(0, 3).map((p, i) => (
                              <a key={i} href={p} target="_blank" rel="noopener noreferrer">
                                <img src={p} alt="" style={{ width: 36, height: 36, objectFit: 'cover', borderRadius: 4 }} />
                              </a>
                            ))}
                            {l.photos.length > 3 && <span style={{ fontSize: 11, color: 'var(--text-3)', alignSelf: 'center' }}>+{l.photos.length - 3}</span>}
                          </div>
                        ) : <span style={{ color: 'var(--text-3)', fontSize: 12 }}>None</span>}
                      </td>
                      <td style={{ padding: '10px 12px' }}><span className={statusClass(l.status)}>{l.status}</span></td>
                      <td style={{ padding: '10px 12px' }}>
                        <button className="btn-outline btn-sm" onClick={() => setEditingLead(l)}>Edit</button>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </div>
      )}

      {/* Modals */}
      {(editingListing || showAdd) && (
        <Modal title={editingListing ? 'Edit Listing' : 'Add Listing'} onClose={() => { setEditingListing(null); setShowAdd(false); }}>
          <ListingForm
            data={editingListing || { name: '', brand: '', year: 2024, price: '', condition: '', battery: '', mileage: '', location: '', description: '', category: 'surron', status: 'Listed', photos: [] }}
            onSave={handleSaveListing}
            onCancel={() => { setEditingListing(null); setShowAdd(false); }}
          />
        </Modal>
      )}

      {editingLead && (
        <Modal title="Edit Lead" onClose={() => setEditingLead(null)}>
          <LeadForm data={editingLead} onSave={handleUpdateLead} onCancel={() => setEditingLead(null)} />
        </Modal>
      )}
    </div>
  );
}

function Modal({ title, children, onClose }) {
  return (
    <div className="modal-overlay" onClick={onClose}>
      <div className="modal-content fade-in" style={{ padding: 24 }} onClick={e => e.stopPropagation()}>
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 20 }}>
          <h3 style={{ fontSize: 18 }}>{title}</h3>
          <button onClick={onClose} style={{ background: 'none', border: 'none', color: 'var(--text-2)', cursor: 'pointer', fontSize: 18 }}>✕</button>
        </div>
        {children}
      </div>
    </div>
  );
}

function ListingForm({ data, onSave, onCancel }) {
  const [form, setForm] = useState({ ...data });
  const [newFiles, setNewFiles] = useState([]);
  const [newPreviews, setNewPreviews] = useState([]);
  const [saving, setSaving] = useState(false);
  const fileRef = useRef(null);
  const set = (k, v) => setForm(p => ({ ...p, [k]: v }));

  const handleNewFiles = (e) => {
    const selected = Array.from(e.target.files).slice(0, 10);
    setNewFiles(prev => [...prev, ...selected]);
    setNewPreviews(prev => [...prev, ...selected.map(f => URL.createObjectURL(f))]);
  };

  const removeExistingPhoto = (idx) => {
    set('photos', (form.photos || []).filter((_, i) => i !== idx));
  };

  const removeNewFile = (idx) => {
    setNewFiles(prev => prev.filter((_, i) => i !== idx));
    setNewPreviews(prev => prev.filter((_, i) => i !== idx));
  };

  const handleSave = async () => {
    setSaving(true);
    await onSave(form, newFiles);
    setSaving(false);
  };

  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: 12 }}>
      <input placeholder="Bike name *" value={form.name} onChange={e => set('name', e.target.value)} />
      <div className="grid-2">
        <select value={form.brand} onChange={e => set('brand', e.target.value)}>
          <option value="">Brand *</option>{BRANDS.map(b => <option key={b} value={b}>{b}</option>)}
        </select>
        <input placeholder="Year" value={form.year} onChange={e => set('year', e.target.value)} />
      </div>
      <input placeholder="Price (AED) *" type="number" value={form.price} onChange={e => set('price', e.target.value)} />
      <div className="grid-2">
        <select value={form.condition} onChange={e => set('condition', e.target.value)}>
          <option value="">Condition</option>{CONDITIONS.map(c => <option key={c} value={c}>{c}</option>)}
        </select>
        <select value={form.battery} onChange={e => set('battery', e.target.value)}>
          <option value="">Battery</option>{BATTERY_CONDITIONS.map(c => <option key={c} value={c}>{c}</option>)}
        </select>
      </div>
      <input placeholder="Mileage (e.g. 320 km)" value={form.mileage} onChange={e => set('mileage', e.target.value)} />
      <input placeholder="Location (e.g. Dubai Marina)" value={form.location} onChange={e => set('location', e.target.value)} />
      <textarea placeholder="Description" value={form.description} onChange={e => set('description', e.target.value)} />
      <div className="grid-2">
        <select value={form.category} onChange={e => set('category', e.target.value)}>
          {CATEGORIES.map(c => <option key={c.id} value={c.id}>{c.label}</option>)}
        </select>
        <select value={form.status} onChange={e => set('status', e.target.value)}>
          {STATUSES.map(s => <option key={s} value={s}>{s}</option>)}
        </select>
      </div>

      {/* Photos */}
      <div>
        <label style={{ fontSize: 12, color: 'var(--text-2)', marginBottom: 6, display: 'block' }}>Photos</label>

        {/* Existing photos */}
        {form.photos && form.photos.length > 0 && (
          <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap', marginBottom: 8 }}>
            {form.photos.map((url, i) => (
              <div key={i} style={{ position: 'relative' }}>
                <img src={url} alt={`Photo ${i + 1}`} style={{ width: 80, height: 80, objectFit: 'cover', borderRadius: 8, border: '1px solid var(--dark-4)' }} />
                <button onClick={() => removeExistingPhoto(i)} style={{ position: 'absolute', top: -6, right: -6, width: 20, height: 20, borderRadius: '50%', background: 'var(--danger)', color: 'white', border: 'none', fontSize: 11, cursor: 'pointer', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>✕</button>
              </div>
            ))}
          </div>
        )}

        {/* New photos to upload */}
        {newPreviews.length > 0 && (
          <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap', marginBottom: 8 }}>
            {newPreviews.map((src, i) => (
              <div key={i} style={{ position: 'relative' }}>
                <img src={src} alt={`New ${i + 1}`} style={{ width: 80, height: 80, objectFit: 'cover', borderRadius: 8, border: '2px solid var(--volt)' }} />
                <button onClick={() => removeNewFile(i)} style={{ position: 'absolute', top: -6, right: -6, width: 20, height: 20, borderRadius: '50%', background: 'var(--danger)', color: 'white', border: 'none', fontSize: 11, cursor: 'pointer', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>✕</button>
              </div>
            ))}
          </div>
        )}

        <div className="photo-upload-zone" onClick={() => fileRef.current?.click()} style={{ padding: 16 }}>
          <p style={{ color: 'var(--text-2)', fontSize: 13 }}>📷 Click to add photos</p>
        </div>
        <input ref={fileRef} type="file" accept="image/*" multiple onChange={handleNewFiles} style={{ display: 'none' }} />
      </div>

      <div style={{ display: 'flex', gap: 12, marginTop: 8 }}>
        <button className="btn-volt" style={{ flex: 1 }} onClick={handleSave} disabled={saving}>
          {saving ? 'Saving...' : 'Save'}
        </button>
        <button className="btn-outline" onClick={onCancel}>Cancel</button>
      </div>
    </div>
  );
}

function LeadForm({ data, onSave, onCancel }) {
  const [form, setForm] = useState({ ...data });
  const set = (k, v) => setForm(p => ({ ...p, [k]: v }));

  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: 12 }}>
      <div style={{ background: 'var(--dark-3)', padding: 16, borderRadius: 8 }}>
        <p style={{ fontSize: 14, fontWeight: 500, marginBottom: 4 }}>{form.name}</p>
        <p style={{ fontSize: 13, color: 'var(--text-2)' }}>{form.phone} — {form.brand} {form.model} ({form.year})</p>
        <p style={{ fontSize: 13, color: 'var(--volt)', marginTop: 4 }}>Asking: AED {Number(form.askingPrice).toLocaleString()}</p>
        <p style={{ fontSize: 12, color: 'var(--text-3)', marginTop: 4 }}>{form.condition} · Battery: {form.battery} · {form.location}</p>
        {form.reason && <p style={{ fontSize: 12, color: 'var(--text-3)', marginTop: 4 }}>Reason: {form.reason}</p>}
      </div>

      {/* Show submitted photos */}
      {form.photos && form.photos.length > 0 && (
        <div>
          <label style={{ fontSize: 12, color: 'var(--text-2)', marginBottom: 6, display: 'block' }}>Submitted Photos</label>
          <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap' }}>
            {form.photos.map((url, i) => (
              <a key={i} href={url} target="_blank" rel="noopener noreferrer">
                <img src={url} alt={`Photo ${i + 1}`} style={{ width: 80, height: 80, objectFit: 'cover', borderRadius: 8, border: '1px solid var(--dark-4)' }} />
              </a>
            ))}
          </div>
        </div>
      )}

      <div>
        <label style={{ fontSize: 12, color: 'var(--text-2)', marginBottom: 6, display: 'block' }}>Status</label>
        <select value={form.status} onChange={e => set('status', e.target.value)}>
          {STATUSES.map(s => <option key={s} value={s}>{s}</option>)}
        </select>
      </div>
      <div>
        <label style={{ fontSize: 12, color: 'var(--text-2)', marginBottom: 6, display: 'block' }}>Notes</label>
        <textarea value={form.notes || ''} onChange={e => set('notes', e.target.value)} placeholder="Add notes about this lead..." />
      </div>

      <div style={{ display: 'flex', gap: 12, marginTop: 8 }}>
        <button className="btn-volt" style={{ flex: 1 }} onClick={() => onSave(form)}>Update</button>
        <a href={`https://wa.me/${form.phone?.replace(/\D/g, '')}`} target="_blank" rel="noopener noreferrer" style={{ textDecoration: 'none' }}>
          <button className="btn-wa btn-sm">💬 Chat</button>
        </a>
        <button className="btn-outline" onClick={onCancel}>Cancel</button>
      </div>
    </div>
  );
}
